// ENG-7401: instrument the integration-add -> session-restart path so the
// dominant cost of the "2+ min to restart" symptom is MEASURED, not assumed.
//
// Adding a Composio toolkit stamps host_agents.restart_requested_at, the manager
// re-renders .mcp.json, injects a restart notice, waits a fixed delay, kills the
// tmux session, respawns claude, and then verifies the new session bound the MCP
// tools. Any of those stages could dominate the wall-clock the operator watches
// on the "Waiting for ... to load" modal. This tracker stamps each stage and
// emits a single greppable `[restart-timing]` line per transition so a real add
// can be broken down from manager.log:
//
//   [restart-timing] 'agent' begin reason="new MCP servers" delivered=true injectDelayMs=8000 pickup=1240ms (request->render)
//   [restart-timing] 'agent' stop +8130ms (schedule->stop; inject/gate delay)
//   [restart-timing] 'agent' tools-bound +39210ms (stop->bound: respawn + MCP bind) — total 48580ms (request->tools-live)
//
// It is pure telemetry: it never changes restart behaviour and never throws into
// the restart path (all emit sites in manager-worker.ts are best-effort). The
// timing math is unit-tested in restart-timing.test.ts.

export type RestartTimingOutcome = 'tools-bound' | 'failed';

interface ActiveTrack {
  reason: string;
  // API stamp (host_agents.restart_requested_at) = the instant the modal's clock
  // starts. Null when the restart was observed via the poll rather than the
  // doorbell, in which case the total is measured from `startedAtMs` instead.
  requestedAtMs: number | null;
  // When begin() fired: the poll re-rendered .mcp.json and scheduled the restart.
  startedAtMs: number;
  injectDelayMs: number | null;
  delivered: boolean | null;
  stopAtMs: number | null;
}

// The API's restart_requested_at, captured at doorbell pickup, waiting for the
// matching begin() from the poll's render. Kept separate from `tracks` so a
// request that never reaches the render block (e.g. a value-only .mcp.json
// rotation that is intentionally skipped) simply expires unused rather than
// leaving a half-open timing track. Entries older than REQUESTED_TTL_MS are
// ignored so a stale stamp can't attach to an unrelated later restart.
const pendingRequested = new Map<string, { requestedAtMs: number; notedAtMs: number }>();
const tracks = new Map<string, ActiveTrack>();

// A doorbell stamp that hasn't been consumed by a render within this window is
// treated as stale (the restart it belonged to was skipped or already serviced).
const REQUESTED_TTL_MS = 5 * 60_000;

function fmtMs(ms: number): string {
  return `${Math.round(ms)}ms`;
}

/**
 * Record the API's restart_requested_at (the modal-clock origin) at doorbell
 * pickup so the subsequent begin() can measure doorbell-pickup -> render latency
 * and anchor the total to what the operator actually sees. Best-effort: an
 * unparseable timestamp is ignored.
 */
export function noteRestartRequested(
  codeName: string,
  requestedAtIso: string,
  now: number = Date.now(),
): void {
  const requestedAtMs = Date.parse(requestedAtIso);
  if (Number.isNaN(requestedAtMs)) return;
  pendingRequested.set(codeName, { requestedAtMs, notedAtMs: now });
}

/**
 * Mark the start of a measured restart: the poll has re-rendered .mcp.json,
 * delivered (or failed to deliver) the restart notice, and is about to schedule
 * the session restart with `injectDelayMs`. Overwrites any prior active track
 * for this agent (a fresh integration change re-anchors the clock).
 */
export function beginRestartTiming(
  codeName: string,
  opts: { reason: string; delivered: boolean | null; injectDelayMs: number | null },
  log: (msg: string) => void,
  now: number = Date.now(),
): void {
  const pending = pendingRequested.get(codeName);
  pendingRequested.delete(codeName);
  const requestedAtMs =
    pending && now - pending.notedAtMs <= REQUESTED_TTL_MS ? pending.requestedAtMs : null;

  tracks.set(codeName, {
    reason: opts.reason,
    requestedAtMs,
    startedAtMs: now,
    injectDelayMs: opts.injectDelayMs,
    delivered: opts.delivered,
    stopAtMs: null,
  });

  const parts = [
    `reason="${opts.reason}"`,
    `delivered=${opts.delivered === null ? 'unknown' : opts.delivered}`,
    `injectDelayMs=${opts.injectDelayMs ?? 'n/a'}`,
  ];
  if (requestedAtMs !== null) {
    parts.push(`pickup=${fmtMs(now - requestedAtMs)} (request->render)`);
  }
  log(`[restart-timing] '${codeName}' begin ${parts.join(' ')}`);
}

/**
 * Mark the moment the tmux session was killed (the restart timer fired and
 * stopPersistentSession ran). No-op — no line emitted — if this agent has no
 * active measured track, which scopes the telemetry to restarts we chose to
 * measure rather than every scheduleSessionRestart caller.
 */
export function markRestartStopped(
  codeName: string,
  log: (msg: string) => void,
  now: number = Date.now(),
): void {
  const track = tracks.get(codeName);
  if (!track) return;
  // A second stop for the same track (e.g. a bind-remediation re-respawn) keeps
  // the original schedule anchor so the total still reflects the full wait, but
  // report the delta from this stop's schedule anchor as measured from begin.
  track.stopAtMs = now;
  log(
    `[restart-timing] '${codeName}' stop +${fmtMs(now - track.startedAtMs)} ` +
    `(schedule->stop; inject/gate delay)`,
  );
}

/**
 * Close out a measured restart. `tools-bound` is the success terminal (respawn
 * verification confirmed the new session bound its MCP tools); `failed` is the
 * bounded give-up (never respawned healthy). Emits the stop->bound delta (the
 * respawn + MCP-bind cost, usually the dominant one) and the end-to-end total.
 * No-op if there is no active track for this agent.
 */
export function finishRestartTiming(
  codeName: string,
  outcome: RestartTimingOutcome,
  detail: string | undefined,
  log: (msg: string) => void,
  now: number = Date.now(),
): void {
  const track = tracks.get(codeName);
  if (!track) return;
  tracks.delete(codeName);

  const originMs = track.requestedAtMs ?? track.startedAtMs;
  const originLabel = track.requestedAtMs !== null ? 'request->tools-live' : 'schedule->tools-live';
  const totalMs = now - originMs;
  const stopToBound = track.stopAtMs !== null ? now - track.stopAtMs : null;
  const suffix = detail ? ` (${detail})` : '';

  if (outcome === 'tools-bound') {
    const boundPart =
      stopToBound !== null
        ? `+${fmtMs(stopToBound)} (stop->bound: respawn + MCP bind)`
        : `(no stop observed)`;
    log(
      `[restart-timing] '${codeName}' tools-bound ${boundPart} — ` +
      `total ${fmtMs(totalMs)} (${originLabel})${suffix}`,
    );
  } else {
    const boundPart =
      stopToBound !== null ? `+${fmtMs(stopToBound)} since stop` : `(no stop observed)`;
    log(
      `[restart-timing] '${codeName}' FAILED to bind ${boundPart} — ` +
      `total ${fmtMs(totalMs)} (${track.requestedAtMs !== null ? 'request' : 'schedule'}->give-up)${suffix}`,
    );
  }
}

/**
 * Drop any active/pending timing state for an agent without emitting a terminal
 * line. Called from the manager's teardown paths (pause / revoke / unassign /
 * shutdown) where the restart is deliberately not coming back, so a cancelled
 * restart never leaves a dangling track to attach to a later, unrelated one.
 */
export function clearRestartTiming(codeName: string): void {
  tracks.delete(codeName);
  pendingRequested.delete(codeName);
}

// Test-only: reset all module state between cases.
export function __resetRestartTimingForTest(): void {
  tracks.clear();
  pendingRequested.clear();
}
